import { Dialog, IconButton } from '@mui/material'
import { Close } from '@emotion-icons/material/Close'
import { Upload } from '@emotion-icons/heroicons-outline/Upload'
import React, { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { useSaveUserList } from 'src/components/user-list-modal/hooks'
import { TextFormField } from 'src/components/text-from-field'
import * as userListModalAtoms from 'src/atoms/user-lists/user-list-modal'
import * as atoms from 'src/atoms/user-lists/user-lists-modal'
import styled from '@emotion/styled'
import { Colors } from 'src/components/theme'
import { Typography } from '@mui/material'
import { Flex } from 'src/components/flex-containers'
import { UserList } from 'src/common-services'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { DividerText } from 'src/v2/components/divider-text'

type UserListFormValues = {
  id?: string
  listName: string
  listDescription?: string
}

const Modal = styled.div`
  padding: 15px;
  width: 290px;
  background-color: ${Colors.bgWhite};
`

const ModalHeading = styled(Flex)`
  margin-bottom: 10px;
  align-items: center;
  justify-content: space-between;
`

const CloseButtonWrapper = styled.div`
  cursor: pointer;
`

const FormWrapper = styled.div`
  & button {
    margin-top: 10px;
  }
`

const HiddenTextFormField = styled(TextFormField)`
  display: none;
`

export const UserListModal: React.FC = () => {
  const [state, setState] = useRecoilState(userListModalAtoms.state)
  const setSelectedListId = useSetRecoilState(atoms.selectedListId)
  const setUploadSection = useSetRecoilState(atoms.uploadSection)
  const setOpen = useSetRecoilState(atoms.open)
  const saveAndUpload = useRef(false)
  const { mutate: saveUserList, isLoading } = useSaveUserList({
    onSuccess: (list: UserList) => {
      if (list.id && saveAndUpload.current === true) {
        setSelectedListId(list.id)
        setUploadSection('upload-csv')
        setOpen(true)
      }
      setState(null)
      reset()
    },
  })
  const formMethods = useForm<UserListFormValues>({
    defaultValues: {
      listName: undefined,
    },
    mode: 'all',
  })
  const { control, handleSubmit, reset } = formMethods
  const modalTitle = !state?.name ? 'Create New List' : 'Edit List'

  const handleFormSubmit = (values: UserListFormValues) => {
    saveUserList({ id: values.id, name: values.listName, description: values.listDescription })
  }

  const handleSave = () => {
    saveAndUpload.current = false
    void handleSubmit(handleFormSubmit)()
  }

  const handleSaveAndUpload = () => {
    saveAndUpload.current = true
    void handleSubmit(handleFormSubmit)()
  }

  const handleClose = () => {
    setState(null)
    reset()
  }

  useEffect(() => {
    reset({ id: state?.id, listName: state?.name, listDescription: state?.description })
  }, [state])

  return (
    <Dialog open={state !== null} onBackdropClick={handleClose} onClose={handleClose}>
      <Modal>
        <ModalHeading>
          <Typography variant="title2" color={Colors.neutral800}>
            {modalTitle}
          </Typography>
          <CloseButtonWrapper>
            <IconButton onClick={handleClose} size="small">
              <Close size={16} />
            </IconButton>
          </CloseButtonWrapper>
        </ModalHeading>
        <FormWrapper>
          <FormProvider {...formMethods}>
            <form>
              <HiddenTextFormField name="id" control={control} />
              <TextFormField
                autoFocus
                name="listName"
                placeholder="List Name"
                control={control}
                rules={{
                  required: { value: true, message: 'List name is required' },
                  maxLength: { value: 40, message: 'List name is too long' },
                }}
                disabled={isLoading}
                fullWidth
              />
              <TextFormField
                name="listDescription"
                placeholder="List Description (optional)"
                control={control}
                rules={{
                  maxLength: { value: 60, message: 'Description is too long' },
                }}
                disabled={isLoading}
                fullWidth
                multiline
                rows={2}
              />
              <ButtonWithIcon inverted disabled={isLoading} fullWidth onClick={handleSave}>
                Save
              </ButtonWithIcon>
              {!state?.name && (
                <>
                  <DividerText>OR</DividerText>
                  <ButtonWithIcon disabled={isLoading} fullWidth onClick={handleSaveAndUpload}>
                    <Upload size={16} />
                    Save and Upload Locations
                  </ButtonWithIcon>
                </>
              )}
            </form>
          </FormProvider>
        </FormWrapper>
      </Modal>
    </Dialog>
  )
}
